import React, { useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { AuthContext } from "../../../../../auth-context";
import { DashboardContext } from "../../../dashboard-context";
import { Action } from "../../../../../components/action-button";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import { addSurveyAssignment, deleteSurveyAssignment, loadProfile } from "../../../../../api";
import { SelectUsers } from "../../campaign/select-users";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableHeadRow,
  TableBodyRow,
  HeadCell,
  DataCell,
  BulkEdit,
  BulkAction,
  WhiteCheckbox,
} from "../../../../../components/table";
import Modal from "../../../../../components/modal";

const Container = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 1600px;
  padding: 0px 16px 64px 16px;
`;

const TableTitle = styled.h2`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px auto;
  color: #555;
  font-family: "DM Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 500;

  button {
    margin-left: 16px;
  }
`;

const CustomTableContainer = styled(TableContainer)`
  height: 600px;
  max-height: 60vh;
`;

const DeleteAssignmentsButton = styled(BulkAction)`
  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const AssignedClients = (props) => {
  const { user, updateUser } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showAddClients, setShowAddClients] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingRequest = useRef(false);

  useEffect(() => {
    setSelectedClients([]);
    setShowAddClients(false);
  }, [props.open, props.surveyID]);

  const updateAssignments = async (accountIDs, adding) => {
    if (pendingRequest.current || accountIDs.length === 0) return;
    pendingRequest.current = true;
    setSaving(true);
    try {
      if (adding) {
        await addSurveyAssignment(user.id, props.surveyID, { account_ids: accountIDs });
      } else {
        await deleteSurveyAssignment(user.id, accountIDs);
      }
      const res = await loadProfile(user.id);
      updateUser(res.data.profile);
      setSelectedClients([]);
      if (adding) setShowAddClients(false);
      openSnackbar(adding ? "Check-in form assigned successfully." : "Clients removed from check-in form", false);
    } catch (err) {
      openSnackbar(adding ? "There was a problem assigning your check-in form." : "Error removing client from check-in form", true);
    } finally {
      pendingRequest.current = false;
      setSaving(false);
    }
  };

  const handleSelect = (user_id) => {
    if (selectedClients.includes(user_id)) {
      setSelectedClients(selectedClients.filter((id) => id !== user_id));
    } else {
      setSelectedClients([...selectedClients, user_id]);
    }
  };

  const handleSelectAll = () => {
    const allUsers = [];
    const assignedClients = user.accounts.filter((account) => {
      return account?.survey?.assigned_survey_id === props.surveyID;
    });
    assignedClients.forEach((entry) => {
      allUsers.push(entry.id);
    });

    if (selectedClients.length === allUsers.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(allUsers);
    }
  };

  const assignedClients = user.accounts.filter((account) => {
    return account?.survey?.assigned_survey_id === props.surveyID;
  });
  const currentSelected = user.accounts.filter(
    (account) => account.survey?.assigned_survey_id
  );

  return (
    <>
    <Modal
      open={props.open}
      onClose={() => { if (!pendingRequest.current) props.onClose(); }}
      maxWidth="lg"
      hideAction={true}
    >
      <Container>
        <TableTitle>
          Manage Assignments
          <BulkEdit disabled={saving} onClick={() => setShowAddClients(true)}>
            Add clients
          </BulkEdit>
            <DeleteAssignmentsButton
              disabled={saving || selectedClients.length === 0}
              onClick={() => updateAssignments(selectedClients, false)}
            >
              Delete selected check-in form assignment(s)
            </DeleteAssignmentsButton>
        </TableTitle>
        <CustomTableContainer>
          <Table>
            <TableHead>
              <TableHeadRow>
                  <HeadCell>
                    <WhiteCheckbox
                      style={{ color: "#FFF" }}
                      checked={assignedClients.length > 0 && selectedClients.length === assignedClients.length}
                      disabled={saving || assignedClients.length === 0}
                      onChange={handleSelectAll}
                      inputProps={{ "aria-label": "checkbox" }}
                    />
                  </HeadCell>
                <HeadCell>Email Address</HeadCell>
                <HeadCell>Name</HeadCell>
                <HeadCell>Actions</HeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {assignedClients.length === 0 && (
                <TableBodyRow>
                  <DataCell colSpan={4} style={{ textAlign: "center" }}>
                    No clients are assigned to this check-in form.
                  </DataCell>
                </TableBodyRow>
              )}
              {assignedClients.map((client) => {
                return (
                  <TableBodyRow key={client.id}>
                      <DataCell>
                        <WhiteCheckbox
                          checked={selectedClients.includes(client.id)}
                          disabled={saving}
                          onChange={() => handleSelect(client.id)}
                          inputProps={{ "aria-label": "checkbox" }}
                        />
                      </DataCell>
                    <DataCell>{client.email_address}</DataCell>
                    <DataCell>{client.name}</DataCell>
                    <DataCell>
                      <Action
                        icon={<DeleteIcon />}
                        iconColor="#da0f0f"
                        text="Delete"
                        disabled={saving}
                        onClick={() => updateAssignments([client.id], false)}
                      />
                    </DataCell>
                  </TableBodyRow>
                );
              })}
            </TableBody>
          </Table>
        </CustomTableContainer>
      </Container>
    </Modal>
    {showAddClients && (
      <SelectUsers
        heading="Select clients to be assigned this check-in form"
        currentSelected={currentSelected}
        submit={(selected) => updateAssignments(selected, true)}
        open={props.open && showAddClients}
        onClose={() => { if (!pendingRequest.current) setShowAddClients(false); }}
        disabled={saving}
        isLoading={saving}
        requireSelection
      />
    )}
    </>
  );
};

export default withRouter(AssignedClients);
